import { Fragment, useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import Form from "../../../forms/Form";
import Select from "../../../forms/Select";
import ModalFactory from "../../../Modal";
import HamQuest from "../../../api/HamQuestGiver";
import { QuestRoute } from "../../../api/QuestRoutes";
import Checkbox from "../../../forms/Checkbox";
import Input from "../../../forms/Input";
import { HSOptions } from "../../../../constants/HSOptions";
import { toShortDate } from "../../../utility/functions";

// #region styled components
const FormWrapper = styled.div`
    max-width: 900px;
`;

const UpdateMemberAwardInstructions = styled.div`
    display: flex;
    flex-direction: column;    
    gap: 1rem;    
    font-size: 1rem;  
    margin-bottom: 1em;
`;

const UpdateMemberAwardsGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const HeaderWrapper = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1.5fr;
    column-gap: 12px;
    align-items: end; /* key change */
    font-size: 1.1rem;

    > div {
        font-weight: 600;
        padding-bottom: 2px; /* optional micro‑adjust */
    }
`;

const FieldsWrapper = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1.5fr;
    grid-auto-rows: minmax(46px, auto);
    grid
    column-gap: 12px;
    row-gap: 4px;
    align-items: self-end;
    font-size: 1.1rem;
`;

const GridCell = styled.div`
    display: flex;
    align-items: self-end;
    gap: 8px;
`;

const CheckboxWrapper = styled.div`
    width: 56px;
    display: flex;
    justify-content: ;
    align-items: center;
    flex-shrink: 0;
    margin-left: 15px;
`;

const Divider = styled.div`
    grid-column: 1 / -1;
    margin: 12px 0;
    border-top: 2px solid rgba(50, 50, 50, 0.2);
`;

//must override $ position since 
//we not using input label on this form
const PriceInput = styled(Input)`
    max-width: 180px;

    &::placeholder {
        color: rgba(255, 74, 74, 0.3);
        opacity: 1; /* Firefox needs this */
        font-style: italic;
    }

    &:disabled::placeholder {
        opacity: 0;
    }

    + svg[data-icon="dollar-sign"] {  
        transform: translateY(-0.5rem);
        bottom: 0px;
        top: unset;
    }
`;

// endregion



const UpdateMemberAwardsForm = ({
    modalId,
    context,
    formData,
    prepaidMembers,
    billingCycleAwardsList,
    memberAwardsPreselect,
    clearBillingUpdateCache
}) => {
    const memberOptions = useMemo(
        () => (billingCycleAwardsList || []).map(a => ({ value: a.id, label: a.name })),
        [billingCycleAwardsList]
    );

    const modalInputs = useMemo(() => {
        const inputs = {};
        prepaidMembers.forEach(m => {
            inputs[`award_${m.memberId}`] = memberAwardsPreselect[m.memberId]?.awardId ?? '';
            inputs[`useCustomPrice_${m.memberId}`] =
                memberAwardsPreselect[m.memberId]?.useCustomPrice ?? m.useCustomPrice ?? false;

        });
        return inputs;
    }, [memberAwardsPreselect]);

    const [useCustomPriceByMemberId, setUseCustomPriceByMemberId] = useState({});

    useEffect(() => {
        const next = {};
        prepaidMembers.forEach(m => {
            next[m.memberId] = Boolean(modalInputs[`useCustomPrice_${m.memberId}`]);
        });
        setUseCustomPriceByMemberId(next);
    }, [modalInputs]);

    const hasSelection = memberId => Boolean(memberAwardsPreselect[memberId]?.awardId);
    const noselect = prepaidMembers.filter(m => !hasSelection(m.memberId));
    const preselect = prepaidMembers.filter(m => hasSelection(m.memberId));

    const renderMemberRow = m => (
        <Fragment key={m.memberId}>
            <GridCell>{m.name}</GridCell>
            <GridCell>{m.awardName}</GridCell>
            <GridCell>
                <Select name={`award_${m.memberId}`} options={memberOptions} nullable required />
            </GridCell>
            <GridCell>
                <CheckboxWrapper>
                    <Checkbox
                        name={`useCustomPrice_${m.memberId}`}
                        callback={(checked) => {
                            setUseCustomPriceByMemberId(prev => ({
                                ...prev,
                                [m.memberId]: checked
                            }));
                        }}
                    />
                </CheckboxWrapper>
                <PriceInput                
                    name={`customPrice_${m.memberId}`}
                    type="price"
                    disable={!useCustomPriceByMemberId[m.memberId]}
                    required={useCustomPriceByMemberId[m.memberId]}
                    placeholder={m.customPrice && `${m.customPrice}`}
                />
            </GridCell>
        </Fragment>
    );

    const billingCycleLabel = (v) => HSOptions.expressPass.billingCycle.find(x => x.value === Number(v))?.label;

    const handleSubmit = (values) => {
        const baseMembers = formData.members ?? context.user.members;

        const updatedMembers = baseMembers
        .filter(m => prepaidMembers.some(p => p.memberId === m.memberId))
        .map(m => {
            const useCustomPrice = values[`useCustomPrice_${m.memberId}`];
            const customPriceValue = values[`customPrice_${m.memberId}`];

            return {
                memberId: m.memberId,
                awardId: Number(values[`award_${m.memberId}`]),
                useCustomPrice,
                ...(useCustomPrice && customPriceValue !== undefined && customPriceValue !== ''
                    ? { customPrice: Number(customPriceValue) }
                    : {}),
                updateAward: true
            };
        });

        ModalFactory.confirmAndProcess({
            confirmation: {
                type: "warning",
                title: "Billing Cycle Change",
                body: (
                    <UpdateMemberAwardInstructions style={{width: '600px'}}>
                        <span>
                            You are about to change the assigned awards for {updatedMembers.length} loyalty members.
                        </span>

                        {context.user.accountStatus === 'Active' && formData.billingType === "Manual" && (
                            <span>New award items and discounts will be available immediately.</span>
                        )}

                        {context.user.accountStatus === 'Active' && formData.billingType === "Automatic" && (
                            <span>
                                New award package pricing will be charged automatically on <strong>{toShortDate(formData.nextBillingDate ?? formData.billingStartDate)}</strong>.
                                However, new award items and discounts will be available immediately.
                            </span>
                        )}
                    </UpdateMemberAwardInstructions>
                ),
                slideToAccept: true
            },
            promise: {
                title: "Submitting",
                body: "Submitting, please wait.",
                callback: () =>
                    HamQuest.put(
                        QuestRoute.loyaltyUser.info(context.user.loyaltyAccountId),
                        { ...formData, members: updatedMembers }
                    )
                        .then(() => {
                            ModalFactory.close(modalId);
                            clearBillingUpdateCache();
                        })
                        .catch(err => ModalFactory.catch(err))
            },
            confirmed: {
                title: "Settings Update",
                body: "Loyalty account settings have been updated."
            }
        });
    };

    return (
        <FormWrapper>
            <Form name="billingCycleAwardUpdates"
                localForm text="Update" 
                inputs={modalInputs} 
                onSubmit={handleSubmit}
            >
                <UpdateMemberAwardInstructions>
                    <span>
                        Changing to a {billingCycleLabel(formData.billingCycle)} billing cycle requires the assignment of {billingCycleLabel(formData.billingCycle)} awards for Prepaid members.
                    </span>
                    <span>
                        
                        Award equivalents (when available) and <b>Use Custom Price</b> have been preselected per current member settings.
                        To assist, existing custom price placeholders are shown. However, all custom pricing must be manually updated or disabled.
                    </span>
                    <span>
                        Please review the list below and update the assigned awards and custom pricing as necessary before continuing. 
                    </span>
                </UpdateMemberAwardInstructions>

                <UpdateMemberAwardsGrid>
                    <HeaderWrapper>
                        <div>Member Name</div>
                        <div>Current Award</div>
                        <div>New Award</div>
                        <div>Use Custom Price</div>
                    </HeaderWrapper>
                    <FieldsWrapper>
                        {noselect.map(renderMemberRow)}
                        {noselect.length > 0 && preselect.length > 0 && <Divider />}
                        {preselect.map(renderMemberRow)}               
                    </FieldsWrapper>                
                </UpdateMemberAwardsGrid>
            </Form>
        </FormWrapper>
    );
};

export default UpdateMemberAwardsForm;
